<template>
  <div class="relative bg-white/50">
    <!-- month's dots loading (calendar/month-days): spinner over the calendar; pointer-events-none keeps the month
         arrows usable, so the user can keep browsing while it loads -->
    <div v-if="loading" class="absolute inset-0 z-10 flex items-center justify-center bg-[rgba(255,255,255,0.55)] rounded-[20px] pointer-events-none"
      role="status" aria-label="Loading calendar">
      <span class="w-[32px] h-[32px] rounded-full border-[3px] border-[#E4DEFA] border-t-[#8479E1] animate-spin" />
    </div>
    <!-- back to today (top-right, above the month header): selects the current date and shows its month -->
    <!-- relative z: the calendar header below must not cover the button; disabled while today is the selected day -->
    <div class="relative z-[2] flex justify-end px-[14px] pt-[8px] -mb-[18px]">
      <button type="button" :disabled="isTodaySelected"
        class="h-[30px] px-[16px] rounded-full border-1 border-[#7695FF] bg-[#fff] text-[13px] font-roboto text-[#7695FF] cursor-pointer transition duration-300 enabled:hover:bg-[#7695FF] enabled:hover:text-[#fff] disabled:opacity-[0.5] disabled:cursor-default"
        @click="goToToday">
        Today
      </button>
    </div>
    <VCalendar
      ref="calendar"
      is-expanded borderless title-position="center" :show-weeknumbers="false"
      class="custom-calendar bg-transparent py-2.5 !flex-grow !w-full" :attributes="calendarAttributes" :show-overflow-days="true"
      :locale="{ id: 'en', firstDayOfWeek: 1, masks: { weekdays: 'W' } }"
      :initial-page="initialPage"
      @dayclick="handleDateClick" @did-move="handleMonthChange"
    />
  </div>
</template>

<script>
import { Calendar as VCalendar } from 'v-calendar';
import moment from "moment";

export default {
  components: {
    VCalendar
  },
  props: {
    events: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['date-selected', 'month-changed'],
  data() {
    const savedDate = sessionStorage.getItem('dashboard_selected_date') || moment().format("MM-DD-YYYY");
    const savedMoment = moment(savedDate, "MM-DD-YYYY");
    return {
      selectedDate: savedDate,
      initialPage: {
        month: savedMoment.month() + 1,
        year: savedMoment.year()
      }
    };
  },
  computed: {
    isTodaySelected() {
      return this.selectedDate === moment().format("MM-DD-YYYY");
    },
    calendarAttributes() {
      return [
        {
          key: 'selected',
          // Styled via .selected-day; v-calendar's tabindex="0" day is the keyboard-focus day (today on load), not the selection
          highlight: { contentClass: 'selected-day' },
          dates: moment(this.selectedDate, "MM-DD-YYYY").toDate(),
        },
        {
          bar: 'red',
          dates: this.events,
        },
      ];
    },
  },
  methods: {
    // select today first, then move: handleMonthChange keeps the selected day, so it stays today
    async goToToday() {
      const today = moment().format("MM-DD-YYYY");
      this.selectedDate = today;
      this.$emit('date-selected', today);
      await this.$refs.calendar?.move(new Date());
    },
    handleDateClick(day) {
      const formattedDate = moment(day.date).format("MM-DD-YYYY");
      this.selectedDate = formattedDate;
      this.$emit('date-selected', formattedDate);
    },
    handleMonthChange(pages) {
      if (pages && pages.length > 0) {
        const page = pages[0];
        const dateStr = `${page.year}-${String(page.month).padStart(2, '0')}-01`;
        this.$emit('month-changed', dateStr);
        // keep the same day in the new month (20 Oct → 20 Nov; 31 Jan → 28/29 Feb)
        const current = moment(this.selectedDate, "MM-DD-YYYY");
        const month = moment({ year: page.year, month: page.month - 1, day: 1 });
        const target = month.date(Math.min(current.date(), month.daysInMonth())).format("MM-DD-YYYY");
        if (target !== this.selectedDate) {
          this.selectedDate = target;
          this.$emit('date-selected', target, { fromMonthChange: true });
        }
      }
    }
  },
}
</script>

<style>
/* 🎨 Tailwind + custom styling for calendar cells */
.custom-calendar .vc-day-content {
  @apply flex items-center justify-center transition-all 
  duration-150 bg-white !shadow-custom rounded-full
  !relative !my-[10px] !mx-auto font-normal !font-roboto text-[#000000];
  /* Scales with the day column (1/7 of the calendar) instead of fixed 32/40px */
  width: 78% !important;
  height: auto !important;
  aspect-ratio: 1 / 1;
  min-width: 28px;
  max-width: 52px;
  font-size: clamp(13px, 3.4vw, 16px) !important;
}
/* Laptop+: calendar box is fixed width; keep days at Figma desktop size */
@media (min-width: 1024px) {
  .custom-calendar .vc-day-content {
    max-width: 30px;
    min-width: 0;
    font-size: 12px !important;
    margin-top: 4px !important;
    margin-bottom: 4px !important;
  }
  .custom-calendar .vc-weekday {
    font-size: 10px;
    padding-top: 3px;
    padding-bottom: 3px;
  }
  .custom-calendar .vc-weekdays {
    margin-top: 12px;
    margin-bottom: 8px;
  }
  .custom-calendar .vc-title {
    font-size: 15px !important;
  }
}
/* Each day / weekday takes an equal 1/7 share of the row */
.custom-calendar .vc-week > .vc-day,
.custom-calendar .vc-weekdays > .vc-weekday {
  flex: 1 1 0;
  min-width: 0;
}

.custom-calendar .vc-day.is-not-in-month .vc-day-content {
  @apply !text-gray-400 !bg-gray-200 opacity-100 border-white border;
}
.custom-calendar .vc-weekdays {
  @apply my-5 ;
}
.custom-calendar .vc-weekday {
  @apply leading-none font-medium rounded-[16px] font-roboto mx-[2px] tablet:mx-1
  py-[4px] tablet:py-1.5 text-white bg-[#4A628A];
  font-size: 12px;
  max-width: 64px;
  padding-top: 5px;
    padding-bottom: 5px;
}
.custom-calendar .vc-title {
  @apply text-center font-normal !font-roboto !text-[18px] text-[#333333];
}
.custom-calendar .vc-arrow {
  @apply hover:!text-white !text-[#F1C93B] !shadow-custom !rounded-full !bg-white hover:!bg-[#F1C93B] !w-10 !h-10 transition-all duration-300;
}
.custom-calendar .vc-header {
  @apply !flex justify-around !mt-[15px];
}
/* Arrows sit in their own header laid over the title header (v-calendar); push them to the
   edges so long months (September) never run under them. Title stays centred. */
.custom-calendar .vc-pane-header-wrapper .vc-header {
  justify-content: space-between !important;
  padding: 0 14px;
}
.custom-calendar .vc-pane > .vc-header {
  justify-content: center !important;
}
.custom-calendar .vc-header .vc-title { white-space: nowrap; }
.custom-calendar .vc-bars {
  @apply !bg-[#BA90C6] mobile:!w-[6px] tablet:!w-[8px] mobile:!h-[6px] tablet:!h-[8px] rounded-full overflow-hidden absolute right-[7px] top-[12px];
}
.custom-calendar .vc-bar.vc-red {
  @apply !bg-[#BA90C6] mobile:!w-[6px] tablet:!w-[8px] mobile:!h-[6px] tablet:!h-[8px] rounded-full;
}
.custom-calendar .vc-weeks {
  @apply laptop:max-w-[550px] mx-auto;
}
 .vc-weekdays{
  @apply mobile:flex laptop:grid justify-between;
}
.custom-calendar .vc-blue, .custom-calendar .vc-highlights {
  @apply !bg-transparent;
}
.custom-calendar .vc-highlight-bg-light {
  @apply !bg-transparent;
}
.custom-calendar .vc-highlight-bg-solid {
  @apply !bg-white !text-[#333333];
}
.custom-calendar .vc-highlight-content-solid {
  @apply !flex border-0;
}
/* .custom-calendar .vc-day:hover .vc-day-content {
  @apply !bg-[#BA90C6] text-white;
} */
/* extra .vc-day so it beats the [tabindex="-1"] white background rule below */
/* selected day is blue so it doesn't look like the purple event dots */
.custom-calendar .vc-day .vc-day-content.selected-day {
  @apply !bg-[#7695FF] !text-white;
}
/* today: blue ring (filled blue once it is also the selected day) */
.custom-calendar .vc-day.is-today .vc-day-content {
  box-shadow: inset 0 0 0 2px #7695FF !important;
}
.custom-calendar .vc-day.is-today .vc-day-content:not(.selected-day) {
  color: #7695FF !important;
  font-weight: 600 !important;
}
.custom-calendar [tabindex="-1"].vc-day-content {
  @apply !bg-white hover:!bg-[#7695FF] hover:text-white
}
.vc-week, .vc-weekdays{
  @apply !flex mobile:justify-between tablet:justify-around
}
.vc-day-box-center-bottom {
    background: transparent !important;
}
/* Month arrows: small circles (30px mobile, 24px laptop+) */
.custom-calendar .vc-header .vc-arrow {
  width: 30px !important;
  height: 30px !important;
}
.custom-calendar .vc-header .vc-arrow svg {
  width: 16px;
  height: 16px;
}
@media (min-width: 1024px) {
  .custom-calendar .vc-header .vc-arrow {
    width: 24px !important;
    height: 24px !important;
  }
  .custom-calendar .vc-header .vc-arrow svg {
    width: 13px;
    height: 13px;
  }
}
/* Event dot: top-right, just outside the day circle so it never sits on the number */
.custom-calendar .vc-day .vc-bars {
  width: 6px !important;
  height: 6px !important;
  left: auto !important;
  top: 8px !important;
  right: calc(50% - min(39%, 26px) - 1px) !important;
}
.custom-calendar .vc-day .vc-bar.vc-red {
  width: 6px !important;
  height: 6px !important;
}
@media (min-width: 1024px) {
  .custom-calendar .vc-day .vc-bars {
    width: 5px !important;
    height: 5px !important;
    top: 2px !important;
    right: calc(50% - 17px) !important;
  }
  .custom-calendar .vc-day .vc-bar.vc-red {
    width: 5px !important;
    height: 5px !important;
  }
}

@media (max-width: 1400px) {
  .custom-calendar .vc-weekday {
    margin-top: 3px !important;
    margin-bottom: 3px !important;
    font-size: 11px;
  }
}
</style>